import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import '../../styles/marketing.css';
import '../../styles/marketing-partner.css';
import { useScrollReveal } from './useScrollReveal';

/* ============================================================
   Clarid partner-page template — standalone "banker-paper".
   Hard rules (per design handoff): NO main-site nav, NO CTA
   buttons. Logo lockup + contact (name / title / email) only.
   Data-driven so each partner page is a thin config wrapper.
   ============================================================ */

export interface ThesisBeat {
  well: string;
  title: string;
  body: string;
}
export interface DepthItem {
  label: ReactNode;
  txt: string;
}
export interface Proof {
  stat: string;
  desc: string;
}
export interface DiagBox {
  name: string;
  sub: string;
  clarid?: boolean;
}
export interface DiagTag {
  label: string;
  kind?: 'shipped' | 'roadmap';
}
export interface Integration {
  kicker?: string;
  headline: string;
  lead: string;
  barLabel: string;
  flow: DiagBox[];
  tags: DiagTag[];
  note?: string;
}
export interface CredCard {
  num: string;
  title: string;
  body: string;
}
/**
 * An extra card section, rendered after "How it works together" — for content a
 * partner needs stated on its own rather than folded into the thesis (e.g. FNBB:
 * revenue sharing, and a commitment about overlapping services).
 */
export interface PartnerSection {
  /** The words after the section number, e.g. "REVENUE SHARING". */
  kickerLabel: string;
  headline: string;
  lead?: string;
  cards: CredCard[];
  note?: string;
}

export interface PartnerContact {
  name: string;
  title: string;
  email: string;
}

export interface PartnerTemplateProps {
  docTitle: string;
  /** Dual lockup for tech/channel/consulting partners; clarid-only for customer/prospect pages. */
  lockup: 'dual' | 'clarid-only';
  partnerName: string;
  /** Partner logo path; if omitted on a dual lockup, a dashed name slot is shown. */
  partnerLogo?: string;
  eyebrow: string;
  headline: ReactNode;
  lead: string;
  heroNote?: ReactNode;
  thesisKicker: string;
  thesisHeadline: string;
  thesis: ThesisBeat[];
  bringsHeadline: string;
  depth: DepthItem[];
  proofs: Proof[];
  integration?: Integration;
  credibilityHeadline?: string;
  credibility?: CredCard[];
  /** Optional extra sections after "How it works together"; later section numbers shift to fit. */
  sections?: PartnerSection[];
  contact: PartnerContact;
}

/** Shared Clarid credibility cards — true across every partner. */
const DEFAULT_CREDIBILITY: CredCard[] = [
  {
    num: 'In-house',
    title: 'Practitioners, not advisors',
    body: 'A community-bank CMO and former FDIC & Federal Reserve examiners — FLE-credentialed, with 30 years of fair-lending practice at the Fed — full-time on the product, not on an advisory masthead.',
  },
  {
    num: 'Deterministic',
    title: 'Defensible by construction',
    body: 'Multi-provider AI with circuit-breaker fallback means the same input yields the same defensible answer — a partner-level evaluation criterion, not a marketing line.',
  },
  {
    num: 'Evidence-grade',
    title: 'Everything is provable',
    body: 'Immutable audit trail and SHA-256-verified exports. Every check is provable — which is exactly what an examiner asks for.',
  },
];

const CLARID_LOGO = '/clarid-logo-marketing.png';

/**
 * Partner half of the lockup.
 *
 * Falls back to the name slot when the logo is absent OR fails to load. The second case is the
 * one that matters: a page can ship with `partnerLogo` pointed at an asset nobody remembered to
 * commit, and the result is broken-image chrome on a live partner page — in front of that exact
 * partner. A clean text lockup is a much better failure than a torn-page icon.
 */
function PartnerLogoMark({ src, name }: { src?: string; name: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <span className="logo-slot">{name}</span>;
  return <img src={src} alt={name} onError={() => setFailed(true)} />;
}

export default function PartnerTemplate(props: PartnerTemplateProps) {
  const {
    docTitle,
    lockup,
    partnerName,
    partnerLogo,
    eyebrow,
    headline,
    lead,
    heroNote,
    thesisKicker,
    thesisHeadline,
    thesis,
    bringsHeadline,
    depth,
    proofs,
    integration,
    credibilityHeadline = 'Built by a community banker, validated by former examiners.',
    credibility = DEFAULT_CREDIBILITY,
    sections = [],
    contact,
  } = props;

  // Section numbers after D follow however many extra sections the page adds.
  const num = (n: number) => String(n).padStart(2, '0');
  const credNum = num(4 + sections.length);
  const contactNum = num(5 + sections.length);

  useScrollReveal();

  useEffect(() => {
    const prev = document.title;
    document.title = docTitle;
    return () => {
      document.title = prev;
    };
  }, [docTitle]);

  return (
    <div className="clarid-marketing partner-root">
      {/* NAV · lockup only */}
      <header className="pnav">
        <div className="pcontainer pnav-inner">
          <div className="lockup">
            <img src={CLARID_LOGO} alt="clarid.ai" />
            {lockup === 'dual' && (
              <>
                <span className="x">×</span>
                <PartnerLogoMark src={partnerLogo} name={partnerName} />
              </>
            )}
          </div>
        </div>
      </header>

      <main>
        {/* A · HERO */}
        <section className="phero">
          <div className="pcontainer">
            <div className="kicker fade-up" style={{ marginBottom: '2rem' }}>{eyebrow}</div>
            <h1 className="fade-up balance">{headline}</h1>
            <p className="lead pretty fade-up muted">{lead}</p>
            {heroNote && <p className="slot-note fade-up" style={{ marginTop: '1.4rem' }}>{heroNote}</p>}
          </div>
        </section>

        {/* B · WHY THIS PARTNERSHIP */}
        <section className="psection panel-bg hairline-top">
          <div className="pcontainer">
            <div className="sign fade-up" style={{ marginBottom: 'clamp(3rem,5vw,4.5rem)' }}>
              <div className="sign-row">
                <span className="kicker kicker--ink">{thesisKicker}</span>
                <hr className="rule" />
              </div>
              <h2 className="h2 balance" style={{ maxWidth: '24ch' }}>{thesisHeadline}</h2>
            </div>
            <div className="thesis">
              {thesis.map((t, i) => (
                <div className="tcard fade-up" style={{ '--d': `${i * 0.12}s` } as CSSProperties} key={t.title}>
                  <div className="well">{t.well}</div>
                  <h3>{t.title}</h3>
                  <p>{t.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* C · WHAT CLARID BRINGS */}
        <section className="psection">
          <div className="pcontainer">
            <div className="brings-grid">
              <div className="fade-up">
                <div className="sign-row" style={{ marginBottom: '1.8rem' }}>
                  <span className="kicker kicker--ink">§&nbsp;02 · WHAT CLARID BRINGS</span>
                </div>
                <h2 className="h2 balance" style={{ maxWidth: '18ch' }}>{bringsHeadline}</h2>
                <ul className="depth-list">
                  {depth.map((d, i) => (
                    <li key={i}>
                      <span className="dlabel">{d.label}</span>
                      <span className="dtxt">{d.txt}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="fade-up proofs">
                {proofs.map((p) => (
                  <div className="proof" key={p.stat}>
                    <div className="pstat">{p.stat}</div>
                    <div className="pdesc">{p.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* D · HOW IT WORKS TOGETHER */}
        {integration && (
          <section className="psection integration">
            <div className="pcontainer">
              <div className="sign fade-up" style={{ marginBottom: '0.5rem' }}>
                <div className="sign-row">
                  <span className="kicker">{integration.kicker ?? '§ 03 · HOW IT WORKS TOGETHER'}</span>
                  <hr className="rule" style={{ borderColor: 'rgba(243,241,234,0.18)' }} />
                </div>
                <h2 className="h2 balance" style={{ maxWidth: '18ch' }}>{integration.headline}</h2>
              </div>
              <p className="lead fade-up" style={{ maxWidth: '56ch' }}>{integration.lead}</p>
              <div className="diagram scale-in">
                <div className="diag-bar">
                  <span className="dot3"></span><span className="dot3"></span><span className="dot3"></span>
                  <span className="dlbl">{integration.barLabel}</span>
                </div>
                <div className="diag-flow">
                  {integration.flow.map((b, i) => (
                    <Fragment2 key={b.name} last={i === integration.flow.length - 1}>
                      <div className={`diag-box${b.clarid ? ' clarid' : ''}`}>
                        <div className="bname">{b.name}</div>
                        <div className="bsub">{b.sub}</div>
                      </div>
                    </Fragment2>
                  ))}
                </div>
                <div className="diag-tags">
                  {integration.tags.map((t) => (
                    <span className={`diag-tag${t.kind ? ' ' + t.kind : ''}`} key={t.label}>{t.label}</span>
                  ))}
                </div>
              </div>
              {integration.note && (
                <p className="slot-note fade-up" style={{ marginTop: '1.4rem', color: 'rgba(243,241,234,0.4)' }}>{integration.note}</p>
              )}
            </div>
          </section>
        )}

        {/* D2 · EXTRA SECTIONS (optional) */}
        {sections.map((sec, si) => (
          <section className={`psection${si % 2 === 0 ? '' : ' panel-bg'} hairline-top`} key={sec.kickerLabel}>
            <div className="pcontainer">
              <div className="sign fade-up" style={{ marginBottom: sec.lead ? '1.6rem' : 'clamp(3rem,5vw,4.5rem)' }}>
                <div className="sign-row">
                  <span className="kicker kicker--ink">§&nbsp;{num(4 + si)} · {sec.kickerLabel}</span>
                  <hr className="rule" />
                </div>
                <h2 className="h2 balance" style={{ maxWidth: '24ch' }}>{sec.headline}</h2>
              </div>
              {sec.lead && (
                <p className="lead pretty fade-up muted" style={{ maxWidth: '62ch', marginBottom: 'clamp(2.5rem,4vw,3.5rem)' }}>{sec.lead}</p>
              )}
              <div className="cred-grid">
                {sec.cards.map((c, i) => (
                  <div className="cred-card fade-up" style={{ '--d': `${i * 0.12}s` } as CSSProperties} key={c.title}>
                    <span className="cnum">{c.num}</span>
                    <h3>{c.title}</h3>
                    <p>{c.body}</p>
                  </div>
                ))}
              </div>
              {sec.note && <p className="slot-note fade-up" style={{ marginTop: '1.8rem' }}>{sec.note}</p>}
            </div>
          </section>
        ))}

        {/* E · CREDIBILITY */}
        <section className="psection panel-bg hairline-bottom">
          <div className="pcontainer">
            <div className="sign fade-up" style={{ marginBottom: 'clamp(3rem,5vw,4.5rem)' }}>
              <div className="sign-row">
                <span className="kicker kicker--ink">§&nbsp;{credNum} · WHY IT HOLDS UP</span>
                <hr className="rule" />
              </div>
              <h2 className="h2 balance" style={{ maxWidth: '24ch' }}>{credibilityHeadline}</h2>
            </div>
            <div className="cred-grid">
              {credibility.map((c, i) => (
                <div className="cred-card fade-up" style={{ '--d': `${i * 0.12}s` } as CSSProperties} key={c.title}>
                  <span className="cnum">{c.num}</span>
                  <h3>{c.title}</h3>
                  <p>{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* F · CONTACT (name / title / email only) */}
        <section className="psection">
          <div className="pcontainer">
            <div className="contact-grid">
              <div className="fade-up">
                <div className="sign-row" style={{ marginBottom: '1.6rem' }}>
                  <span className="kicker kicker--ink">§&nbsp;{contactNum} · CONTACT</span>
                </div>
                <h2 className="h2 balance" style={{ maxWidth: '16ch' }}>Your point of contact at Clarid.</h2>
              </div>
              <div className="contact-card scale-in">
                <div className="contact-name">{contact.name}</div>
                <div className="contact-title">{contact.title}</div>
                <a className="contact-email" href={`mailto:${contact.email}`}>{contact.email}</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* MINIMAL FOOTER */}
      <footer className="pfooter">
        <div className="pcontainer pfooter-inner">
          <img src={CLARID_LOGO} alt="clarid.ai" />
          <p className="meta" style={{ color: 'var(--ink-4)' }}>© 2026 Clarid, Inc. · Partnership page · clarid.ai</p>
          <div className="plinks">
            <a href="/security">Security</a>
            <a href="/privacy">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Renders a flow box plus a trailing arrow between boxes. */
function Fragment2({ children, last }: { children: ReactNode; last: boolean }) {
  return (
    <>
      {children}
      {!last && <div className="diag-arrow">→</div>}
    </>
  );
}
